import { useEffect, useMemo, useState } from 'react';
import apiFetch from '../utils/apiFetch';

/**
 * 队员消息媒体：按 media_id 批量换取短期签名 URL（POST /api/tracker/media/urls）。
 * 模块级缓存；距过期 < 60 秒时重新签名。接口 503（未配置存储）/ 无权限时返回 error。
 */
const cache = new Map(); // media_id -> { item, until }
const REFRESH_MARGIN_MS = 60 * 1000;

function fresh(id) {
  const c = cache.get(id);
  return c && c.until - Date.now() > REFRESH_MARGIN_MS ? c.item : null;
}

export async function fetchTrackerMediaUrls(ids) {
  const need = ids.filter((id) => !fresh(id));
  if (need.length) {
    const res = await apiFetch('/api/tracker/media/urls', {
      method: 'POST',
      body: JSON.stringify({ media_ids: need })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      const err = new Error(data.error || `HTTP ${res.status}`);
      err.code = data.code;
      throw err;
    }
    (data.items || []).forEach((item) => {
      const until = Date.parse(item.url_expires_at) || Date.now() + 5 * 60 * 1000;
      cache.set(item.media_id, { item, until });
    });
  }
  return ids.map((id) => fresh(id) || cache.get(id)?.item || null);
}

export default function useTrackerMediaUrls(media) {
  const ids = useMemo(
    () => (Array.isArray(media) ? media.map((m) => m && m.media_id).filter(Boolean) : []),
    [media]
  );
  const idsKey = ids.join(',');
  const [state, setState] = useState({ items: [], error: null, loading: false });

  useEffect(() => {
    if (!idsKey) {
      setState({ items: [], error: null, loading: false });
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const load = async () => {
      setState((s) => ({ ...s, loading: true }));
      try {
        const list = idsKey.split(',');
        const items = await fetchTrackerMediaUrls(list);
        if (cancelled) return;
        setState({ items, error: null, loading: false });
        const soonest = Math.min(...list.map((id) => cache.get(id)?.until || Infinity));
        if (Number.isFinite(soonest)) {
          timer = setTimeout(load, Math.max(30 * 1000, soonest - Date.now() - REFRESH_MARGIN_MS + 1000));
        }
      } catch (e) {
        if (!cancelled) setState({ items: [], error: e, loading: false });
      }
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [idsKey]);

  return state;
}